import { EOS_DUPLICATE_MULTIPLIER } from '../data/constants.js';

export const EOS_TIER = {
  NONE: 'none',
  DUPLICATE: 'duplicate'
};

// duplicateMultiplier is per-mode: Classic keeps the frozen v2.4
// value, Advanced Mode uses a weaker discount (canonical-numbers
// section 16.14). Defaulting to the Classic constant means a caller
// that passes nothing cannot change Classic scoring.
// What counts as "the same type" for the duplicate discount. Defaults
// to the plant's own id, which is the rule as written (canonical
// section 6). A plant may name a shared group instead, so that two
// distinct ids can share one crew, one fuel contract and one set of
// spares - which is the thing the discount is modelling.
const eosGroupOf = (id, plantsById) => plantsById[id]?.eosGroupId ?? id;

// firstCopyPaysFull is per-mode and defaults to FALSE, which is the
// rule as written in canonical section 6 and the one Classic is frozen
// on: if a group has two or more copies, EVERY copy is discounted.
//
// Set true, the first copy of a group pays full opex and only each
// ADDITIONAL copy is discounted. Duplication still pays - the shared
// crew, fuel contract and spares are real - but it stops retroactively
// discounting the plant you already owned, which is what made a second
// Geothermal effectively pay for itself twice.
//
// "First" is position in the portfolio array, which is build order and
// is deterministic for a given plan. Retirement removes the earliest
// matching instance, so a surviving later copy simply becomes the one
// that pays full.
export function applyEconomiesOfScale(
  portfolio,
  plantsById,
  duplicateMultiplier = EOS_DUPLICATE_MULTIPLIER,
  firstCopyPaysFull = false
) {
  const idCounts = {};
  for (const id of portfolio) {
    const group = eosGroupOf(id, plantsById);
    idCounts[group] = (idCounts[group] || 0) + 1;
  }
  const seenSoFar = {};
  return portfolio.map((id) => {
    const p = plantsById[id];
    const group = eosGroupOf(id, plantsById);
    const copyIndex = seenSoFar[group] ?? 0;
    seenSoFar[group] = copyIndex + 1;
    const discounted = firstCopyPaysFull
      ? copyIndex >= 1
      : idCounts[group] >= 2;
    const tier = discounted ? EOS_TIER.DUPLICATE : EOS_TIER.NONE;
    const multiplier = discounted ? duplicateMultiplier : 1;
    return {
      id,
      base: p.opex,
      tier,
      multiplier,
      postEos: p.opex * multiplier
    };
  });
}
